/**
 * Picker searches for "Create enquiry from this mail" (`CreateEnquiryBar.tsx`) and, via
 * `usePurchaserPicker`, `SettingsPage.tsx` too.
 *
 * ---- Bug fix: this file used to ALSO write the enquiry, to the wrong doctype ----
 *
 * `useCreateEnquiryFromMail` used to live here and POST `/api/resource/Opportunity` — the OLD
 * doctype. `/rfq/:id` reads via `enquiry.get_enquiry`, which only sees the NEW `Enquiry` doctype,
 * so a created enquiry could never be opened. Deleted rather than fixed in place: the write now
 * goes through `useCreateEnquiry` in `useEnquiryMutations.ts` (`enquiry.create_enquiry`), the same
 * hook every other "new enquiry" path already used, so there is exactly one write path instead of
 * two that could drift apart again. See `useEnquiryMutations.ts`'s `buildCreateEnquiryWireBody`
 * header for what that move cost (`company`/`customer_currency`/`po_currency` have no home on
 * `create_enquiry`; `mail_id` was initially miscategorised into that same gap but does round-trip
 * — see that file's "`mail_id` (2026-08-05)" section).
 *
 * The pickers below are untouched by that fix and still go through `/api/resource/...`, never the
 * gateway: `get_list`/`get_document` and the rest of `quoflow.quoflow.api.generic.*` do not exist
 * on the deployed server (Q22), while the resource API answers for every doctype we need.
 * `vite.config.ts` routes `Customer`, `Company`, `Delivery Location`, `Currency`, `Item` and `User`
 * live for exactly this flow — see the warning there about the blast radius, because it also moves
 * `/requirements` and `/rfq` onto the live bench.
 *
 * `mock-server/seed-data.ts`'s `MAIL` is `[]`, so in mock-only dev there is no message to select
 * and therefore no dialog to open these pickers from — this file's mock-reachability is the same
 * as it always was, unaffected by the write moving out.
 */

import { useQuery } from '@tanstack/react-query'
import { getResource } from './frappe'
import { str } from './mappers/coerce'

/**
 * Pickers hold thousands of rows (the bench has a full customer list), so each query asks the
 * server to narrow rather than pulling everything and filtering in the browser.
 */
const PICKER_LIMIT = 50

/** Frappe's `like` wants the wildcards spelled out; an empty term means "no filter at all". */
const likeFilter = (field: string, term: string) =>
  term.trim() === '' ? [] : [[field, 'like', `%${term.trim()}%`] as [string, string, unknown]]

/** One option in a dialog dropdown. `name` is the Link value actually written to the document. */
export interface PickerOption {
  name: string
  label: string
  /**
   * A second, quieter line under the label — the CONSEQUENCE of picking this option, when the
   * name alone does not carry it.
   *
   * Added 2026-08-11 for the UOM picker, where it is the whole point: a purchaser had to select
   * each unit in turn just to read its conversion factor. With the factor on every option
   * (`1 CASE = 12 TRAY`), one open of the list answers what previously took a click per unit.
   * Optional, and every existing picker omits it.
   */
  sublabel?: string
  /**
   * Marks the option that is already in force — the item's base unit, the current value, whatever
   * "you are here" means for that list. Rendered dimmer and to the right, never as a second line.
   */
  note?: string
}

function toOptions(rows: Record<string, unknown>[], labelField?: string): PickerOption[] {
  return rows.map((row) => {
    const name = str(row.name)
    const label = labelField ? str(row[labelField]) : ''
    return { name, label: label !== '' && label !== name ? `${name} — ${label}` : name }
  })
}

/**
 * A generic master picker. `searchField` is what the typed term filters on — `name` for doctypes
 * whose docname IS the human-readable value (Customer, Company, Currency, Delivery Location), and
 * a separate field where it is not (User's `full_name`).
 */
function useMasterPicker(doctype: string, search: string, searchField = 'name', labelField?: string) {
  return useQuery({
    queryKey: ['picker', doctype, search],
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>(doctype, {
        fields: labelField ? ['name', labelField] : ['name'],
        filters: likeFilter(searchField, search),
        limit: PICKER_LIMIT,
        orderBy: `${searchField} asc`,
      })
      return toOptions(page.data, labelField)
    },
    // Masters barely change within a session; refetching on every dialog open is wasted traffic.
    staleTime: 5 * 60_000,
  })
}

/**
 * One vessel's readable name, for a code already stored on the enquiry.
 *
 * The picker labels its OPTIONS (`1002898 — CLEOPATRA`), but once chosen the field holds the
 * docname — a bare code — and the enquiry reads as `Vessel: 1002898`. This resolves it so the
 * header can show the name beside the code it actually stores.
 *
 * Never falls back to the code as a "name": an unresolved vessel returns `''` and the caller shows
 * the code alone, rather than presenting a number as though it were the vessel's name.
 */
export function useVesselName(code: string | null | undefined) {
  return useQuery({
    queryKey: ['vessel-name', code ?? ''] as const,
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>('Vessel', {
        fields: ['name', 'vessel_name'],
        filters: [['name', '=', code ?? '']],
        limit: 1,
      })
      return str(page.data[0]?.vessel_name)
    },
    enabled: !!code,
    staleTime: 5 * 60_000,
  })
}

// `Customer` has NO unscoped picker here any more (2026-09-15). It read `/api/resource/Customer`
// with no company filter and listed every customer in the ERP; `masters.get_customers` takes a
// mandatory `company` and answers only that company's — see `useCustomerRecord.ts`'s
// `useCustomerSearch`, which every customer picker now closes over its company to call.
// `Company` deliberately has NO picker here. It lives in `useMasters.ts`'s `useCompanyPicker`,
// backed by `lookups.get_companies` — a `quoflow.*` method that also returns each company's
// `default_currency`, which the enquiry header needs and `/api/resource/Company` does not give.
// Two hooks of the same name backed by different endpoints is how they drift apart.
export const useDeliveryLocationPicker = (search: string) => useMasterPicker('Delivery Location', search)
export const useCurrencyPicker = (search: string) => useMasterPicker('Currency', search)
/**
 * Account heads for the quotation's taxes-and-charges rows (`Sales Taxes and Charges`).
 *
 * Scoped to the quotation's company (Akhil, 2026-10-30). Unscoped, it listed every company's
 * chart of accounts, and Frappe rejects a tax row whose account belongs to another company on
 * save.
 *
 * Group accounts can NOT be filtered out here: rfqdev answers `DataError: Field not permitted in
 * query: is_group` (measured 2026-10-30), so a parent like "Bank Accounts" still shows up.
 */
export function accountFilters(search: string, company: string): [string, string, unknown][] {
  const scope = company.trim()
  return [...(scope ? [['company', '=', scope] as [string, string, unknown]] : []), ...likeFilter('name', search)]
}

export function useAccountPicker(search: string, company: string) {
  return useQuery({
    queryKey: ['picker', 'Account', company, search],
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>('Account', {
        fields: ['name'],
        filters: accountFilters(search, company),
        limit: PICKER_LIMIT,
        orderBy: 'name asc',
      })
      return toOptions(page.data)
    },
    staleTime: 5 * 60_000,
  })
}
/** The quotation's `Terms and Conditions` template. */
export const useTermsPicker = (search: string) => useMasterPicker('Terms and Conditions', search)

/**
 * `purchaser` is a Link to `User`. Searching on `full_name` rather than `name` because `name` is
 * the email address — a purchaser looking for "Shiby" should not have to know it.
 */
export const usePurchaserPicker = (search: string) => useMasterPicker('User', search, 'full_name', 'full_name')

/*
  Link fields on the RFQ header card. All four were rendered as FREE TEXT, which cannot work:
  Frappe validates a Link on save, so a typed value that is not an existing docname is rejected.
  All four masters are populated on the bench (Customer Department, Sales Person, Port, Vessel).
*/
export const useCustomerDepartmentPicker = (search: string) => useMasterPicker('Customer Department', search)
export const useSalesPersonPicker = (search: string) => useMasterPicker('Sales Person', search)
export const usePortPicker = (search: string) => useMasterPicker('Port', search)
/*
  Vessel is searched and labelled on `vessel_name`, NOT on the docname.

  `Vessel` is named by its CODE — `1002898`, with `vessel_name: "CLEOPATRA"` beside it (measured
  2026-09-07). Searching `name` therefore meant typing a number nobody remembers, and the picker
  listed bare codes: a purchaser choosing a vessel from `1002898 / 1010258 / …` is guessing.

  `toOptions` renders it as `1002898 — CLEOPATRA`, so the code the enquiry actually stores stays
  visible beside the name people know it by.
*/
export const useVesselPicker = (search: string) => useMasterPicker('Vessel', search, 'vessel_name', 'vessel_name')

/**
 * Items for the line picker. Separate from `useItems` in `./useItems.ts`: that one returns mapped
 * `Item` domain objects for the RFQ screen, whereas this needs `stock_uom` to prefill the line's
 * UOM and is scoped to the dialog's own cache key.
 */
export interface ItemOption {
  item_code: string
  item_name: string
  stock_uom: string
}

export function useItemPicker(search: string) {
  return useQuery({
    queryKey: ['picker', 'Item', search],
    queryFn: async () => {
      const page = await getResource<Record<string, unknown>>('Item', {
        fields: ['name', 'item_name', 'stock_uom'],
        // `item_name` rather than `item_code`: a purchaser reading a customer's mail is looking at
        // descriptions, not codes. Frappe filters are AND-only, so this cannot also OR on the code
        // in one request — a known limitation, not an oversight.
        filters: likeFilter('item_name', search),
        limit: PICKER_LIMIT,
        orderBy: 'item_name asc',
      })
      return page.data.map((row) => ({
        item_code: str(row.name),
        item_name: str(row.item_name),
        stock_uom: str(row.stock_uom),
      }))
    },
    staleTime: 60_000,
  })
}
